/**
 * FilterContextCell — Lazy-loading cell for Explorer DataGrid metrics that
 * return filter context objects instead of full data payloads.
 *
 * Handles three metric types (determined by column name):
 *   - Video → /api/video/playlist → iframe dialog
 *   - ShotChart / iShotChart (legacy: ShotDetailChart) → /api/team_report/shot_detail → shot chart dialog
 *   - ActionChart / iActionChart (legacy: ActionDetailChart) → /api/team_report/action_detail → action chart dialog
 *
 * Per-type charts (PickDetailChart, etc.) still return full data and
 * use the existing ShotDetailChartCell / ActionDetailChartCell components.
 *
 * Since R3 a cell carries its query's context beside the filters:
 *   {"filters": [...], "scope": "nba", "source": "ss" | null, "seasonsBySource": {...}}
 * The cell's `source` wins, then the result's `queryContext.source` (cells cached
 * before R3 have neither key). Forwarding it is what keeps an explicit-source
 * query's chart and video on that provider instead of the season default.
 */

import { useState, useCallback } from "react";
import { Box, CircularProgress, Snackbar, Alert } from "@mui/material";
import PlayCircleOutline from "@mui/icons-material/PlayCircleOutline";
import InsightsOutlined from "@mui/icons-material/InsightsOutlined";
import ShotChartIcon from "../../Icons/ShotChartIcon";
import { ExplorerAPI } from "../../../util/Requests";
import { ExplorerEnvironment } from "../../../data/Cache";
import { normalizeAction } from "../../../TeamReport/ActionDetailModal/types";
import type { ActionDetailResponse } from "../../../TeamReport/ActionDetailModal/types";
import {
  normalizeShot,
  type Shot,
} from "../../../TeamReport/ShotDetailModal/types";
import VideoPlayerDialog, { skippedVideoMessage } from "../VideoPlayerDialog";
import type { VideoPlayerState } from "../VideoPlayerDialog";
import ShotDetailChartDialog from "./ShotDetailChartDialog";
import ActionDetailChartDialog from "./ActionDetailChartDialog";
import type { ValueLookup } from "./VideoCellHandler";
import type {
  ContextFilter,
  ExplorerQueryContext,
  ExplorerSource,
  QueryResolution,
} from "../../../models/Explorer";
import { FeatureFlags } from "../../../util/featureFlags";
import { formatResolution } from "../DevSourceLabel";

/** A lazy cell's JSON payload. */
export interface FilterContextCellData {
  filters?: ContextFilter[];
  scope?: string | null;
  source?: ExplorerSource | null;
  seasonsBySource?: Record<string, number[]>;
}

/**
 * What a lazy call sends for one cell: the result's global filters followed by
 * the cell's, and the source the query was pinned to (cell first).
 */
export function resolveCellRequest(
  cell: FilterContextCellData,
  queryContext?: ExplorerQueryContext,
): { filters: ContextFilter[]; source: ExplorerSource | null } {
  return {
    filters: [...(queryContext?.filters || []), ...(cell.filters || [])],
    source: cell.source ?? queryContext?.source ?? null,
  };
}

/** Dev builds append the provider a detail chart read; product titles never name one. */
function devTitle(title: string, resolution?: QueryResolution): string {
  if (!FeatureFlags.sourceLabel || !resolution) return title;
  const label = formatResolution(resolution);
  return label ? `${title} [dev · ${label}]` : title;
}


// ============================================================================
// Metric type detection from column name
// ============================================================================

type FilterContextMetricType = "video" | "shotDetail" | "actionDetail";

/**
 * Determine the metric type from a column name.
 * Column names follow patterns like: Video-PR, HC_ShotChart,
 * ActionChart-halfcourt, iShotChart, etc.
 * Also matches legacy names: ShotDetailChart, ActionDetailChart.
 */
export function getFilterContextMetricType(
  columnName: string,
): FilterContextMetricType | null {
  // Order matters: check more specific patterns first
  if (/Action(?:Detail)?Chart/i.test(columnName)) return "actionDetail";
  if (/Shot(?:Detail)?Chart/i.test(columnName)) return "shotDetail";
  // A named metric group prefixes its columns (keep_Video), so Video may follow an underscore.
  if (/(?:^|_)Video/i.test(columnName)) return "video";
  return null;
}

// ============================================================================
// Title builder
// ============================================================================

const METRIC_LABELS: Record<FilterContextMetricType, string> = {
  video: "Video",
  shotDetail: "Shot Chart",
  actionDetail: "Action Chart",
};

function buildTitle(
  metricType: FilterContextMetricType,
  cellFilters: Array<{ name: string; op: string; values: any[] }>,
  valueLookup?: ValueLookup,
): string {
  const label = METRIC_LABELS[metricType];
  const parts = cellFilters
    .map((f) => {
      const filterMap = valueLookup?.[f.name];
      const resolved = f.values.map((v) => {
        const str = String(v);
        return filterMap?.get(str) ?? str;
      });
      return resolved.join(", ");
    })
    .filter((v) => v.length > 0);
  if (parts.length === 0) return label;
  return label + " \u2014 " + parts.join(" \u00BB ");
}

// ============================================================================
// Component
// ============================================================================

interface FilterContextCellProps {
  data: string;
  columnName: string;
  metricType: FilterContextMetricType;
  queryContext?: ExplorerQueryContext;
  environment: ExplorerEnvironment;
  valueLookup?: ValueLookup;
}

export default function FilterContextCell({
  data,
  columnName,
  metricType,
  queryContext,
  environment,
  valueLookup,
}: FilterContextCellProps) {
  // Video state (iframe dialog)
  const [videoState, setVideoState] = useState<VideoPlayerState | null>(null);

  // Shot detail state
  const [shotDetailState, setShotDetailState] = useState<{
    shots: Shot[];
    title: string;
  } | null>(null);

  // Action detail state
  const [actionDetailState, setActionDetailState] = useState<{
    data: ActionDetailResponse;
    title: string;
  } | null>(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClick = useCallback(
    async (e: React.MouseEvent) => {
      if (metricType === "video" && environment !== ExplorerEnvironment.NBA)
        return;

      setLoading(true);
      setError(null);
      try {
        const cellData: FilterContextCellData = JSON.parse(data);
        const cellFilters = cellData.filters || [];
        const { filters: combinedFilters, source } = resolveCellRequest(
          cellData,
          queryContext,
        );
        const api = new ExplorerAPI(environment);

        if (metricType === "video") {
          const response = await api.getVideoPlaylistByFilters(combinedFilters, {
            source,
          });
          if (response.error) {
            setError(response.error);
          } else if (response.playlistUrl) {
            if (e.metaKey) {
              setVideoState({
                url: response.playlistUrl,
                title: buildTitle(metricType, cellFilters, valueLookup),
                skippedCount: response.skippedCount ?? 0,
              });
            } else {
              window.open(response.playlistUrl, "_blank");
              if (response.skippedCount) {
                setError(skippedVideoMessage(response.skippedCount));
              }
            }
          }
        } else if (metricType === "shotDetail") {
          const response = await api.getShotDetailByFilters(combinedFilters, {
            source,
          });
          const normalizedShots: Shot[] = (response.shots || [])
            .filter(
              (s: any) =>
                typeof s.x === "number" &&
                typeof s.y === "number" &&
                isFinite(s.x) &&
                isFinite(s.y),
            )
            .map(normalizeShot);
          setShotDetailState({
            shots: normalizedShots,
            title: devTitle(
              buildTitle(metricType, cellFilters, valueLookup),
              response.resolution,
            ),
          });
        } else if (metricType === "actionDetail") {
          const response = await api.getActionDetailByFilters(combinedFilters, {
            source,
          });
          const normalized: ActionDetailResponse = {
            ...response,
            actions: (response.actions || []).map(normalizeAction),
          };
          setActionDetailState({
            data: normalized,
            title: devTitle(
              buildTitle(metricType, cellFilters, valueLookup),
              response.resolution,
            ),
          });
        }
      } catch (err: any) {
        console.error("FilterContextCell error:", {
          metricType,
          columnName,
          data,
          err,
        });
        const errorData = err?.response?.data;
        setError(errorData?.error ?? "Failed to load data");
      } finally {
        setLoading(false);
      }
    },
    [
      data,
      metricType,
      queryContext,
      environment,
      valueLookup,
      columnName,
    ],
  );

  // Video is NBA-only
  if (metricType === "video" && environment !== ExplorerEnvironment.NBA) {
    return null;
  }

  const icon =
    metricType === "video" ? (
      <PlayCircleOutline sx={{ fontSize: "1.05rem" }} />
    ) : metricType === "shotDetail" ? (
      <ShotChartIcon sx={{ fontSize: "1.15rem" }} />
    ) : (
      <InsightsOutlined sx={{ fontSize: "1.05rem" }} />
    );

  return (
    <>
      <Box
        onClick={handleClick}
        className="cell--icon-container"
        sx={{ height: "100%" }}
      >
        {loading ? <CircularProgress size={14} /> : icon}
      </Box>

      {/* Video iframe dialog */}
      {metricType === "video" && (
        <VideoPlayerDialog
          state={videoState}
          onClose={() => setVideoState(null)}
        />
      )}

      {/* Shot detail dialog */}
      {shotDetailState && (
        <ShotDetailChartDialog
          shots={shotDetailState.shots}
          isOpen
          handleClose={() => setShotDetailState(null)}
          title={shotDetailState.title}
        />
      )}

      {/* Action detail dialog */}
      {actionDetailState && (
        <ActionDetailChartDialog
          data={actionDetailState.data}
          isOpen
          handleClose={() => setActionDetailState(null)}
          title={actionDetailState.title}
        />
      )}

      {/* Error snackbar */}
      <Snackbar
        open={Boolean(error)}
        autoHideDuration={5000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setError(null)}
          severity="error"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {error}
        </Alert>
      </Snackbar>
    </>
  );
}
